/* ===========================================================
   editor/extensions.js — what a document can contain

   One list for every mode: Casual and Developer docs share a schema,
   so a doc made in one opens fine in the other. Mode only changes
   menus and a few typing shortcuts ($…$ math), never the content.

   docExtensions() has no DOM needs, so the same list builds the
   schema in tests and for conversions (Yjs ↔ JSON ↔ Markdown).
   =========================================================== */

import { getSchema } from '@tiptap/core';
import { StarterKit } from '@tiptap/starter-kit';
import { TaskList, TaskItem } from '@tiptap/extension-list';
import { TableKit } from '@tiptap/extension-table';
import { Image } from '@tiptap/extension-image';
import { TextAlign } from '@tiptap/extension-text-align';
import { Highlight } from '@tiptap/extension-highlight';
import { Placeholder, CharacterCount } from '@tiptap/extensions';
import { MathInline, MathBlock } from './nodes/math.js';
import { RunnableCode } from './nodes/code.js';
import { Sandbox, SandboxCode } from './nodes/sandbox.js';

const safeUri = url => /^(https?:\/\/|mailto:|\/)/i.test(String(url || '').trim());

export function docExtensions({ placeholder = 'Start writing…' } = {}) {
  return [
    StarterKit.configure({
      codeBlock: false,
      undoRedo: false,             // Yjs keeps its own undo history (per person)
      trailingNode: {},
      link: {
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        defaultProtocol: 'https',
        isAllowedUri: (url, ctx) => safeUri(url) && ctx.defaultValidate(url),
        shouldAutoLink: url => /^https?:\/\//i.test(url) || /^[\w-]+(\.[\w-]+)+/.test(url),
        HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
      },
    }),
    RunnableCode,
    TaskList,
    TaskItem.configure({ nested: true }),
    TableKit.configure({ table: { resizable: true, cellMinWidth: 60 } }),
    Image.configure({ inline: false, allowBase64: false }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Highlight,
    MathInline,
    MathBlock,
    Sandbox,
    SandboxCode,
    Placeholder.configure({ placeholder, showOnlyCurrent: true }),
    CharacterCount,
  ];
}

let schema = null;
export function docSchema() {
  if (!schema) schema = getSchema(docExtensions());
  return schema;
}
